// Local print-agent client.
//
// A branch may name a physical printer per document type. When it has, and the
// agent is running on this PC, printing is silent — no dialog, no picking the
// printer each time. When it is not, we fall back to the browser so a front desk
// without the agent still works.
//
// The agent is per-PC, not a server: 127.0.0.1 means "this machine", and the
// printers are the ones installed on it.
//
// The agent's port and AGENT_BASE here are one setting in two places — change
// them together or the client stops finding the agent. On the agent side the
// knob is PRINT_AGENT_PORT (env), `port` in its agent-config.json, or
// DEFAULT_PORT in print-agent/src/config.js; there is no AGENT_PORT constant.

const AGENT_BASE = 'http://127.0.0.1:8083';

export const PRINTER_TYPE_COLUMN = {
  receipt: 'receipt_printer_name',
  label: 'label_printer_name',
  document: 'document_printer_name',
};

// Optional shared secret, when the agent on this PC is configured to require
// one (print-agent/agent-config.json). It is per-PC setup, not per-user state:
// whoever installs the agent puts the same value here, once, in this browser.
//
// Honest about what it buys: a secret the browser must be able to read is a
// secret anything else with access to that browser profile can read. It raises
// the bar against a local process that was not told the value; the origin
// allowlist in the agent is what actually stops a hostile web page. An agent
// with no token configured ignores the header entirely.
function agentToken() {
  try {
    return localStorage.getItem('print_agent_token') || '';
  } catch {
    // Storage can throw in a locked-down or private context. No token is a
    // valid state, so this must never break printing.
    return '';
  }
}

function withTimeout(ms) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, done: () => clearTimeout(timer) };
}

export async function agentHealthy(timeoutMs = 1500) {
  const { signal, done } = withTimeout(timeoutMs);
  try {
    const res = await fetch(`${AGENT_BASE}/health`, { signal });
    if (!res.ok) return false;
    const body = await res.json();
    return !!body.ok;
  } catch {
    return false;
  } finally {
    done();
  }
}

// Always resolves to an array of printer NAME strings.
//
// The agent returns richer rows — `{ name, isDefault }` — because its /printers
// route reports what Electron enumerates. Callers here only ever need the name
// (it is what goes into branches.*_printer_name), and a caller that rendered the
// raw object would put "[object Object]" in a dropdown. Normalising once, here,
// is why every caller can treat the result as plain strings. Objects without a
// usable name are dropped rather than rendered as blanks.
//
// Bounded like agentHealthy: enumerating printers can involve a hidden window
// and a driver query, and a hung agent must not leave the Branches form waiting
// on a dropdown that never fills. Giving up returns [], which is the same
// answer as "no printers", and the caller falls back to a text input.
export async function listAgentPrinters(timeoutMs = 3000) {
  const { signal, done } = withTimeout(timeoutMs);
  try {
    const res = await fetch(`${AGENT_BASE}/printers`, { signal });
    if (!res.ok) return [];
    const body = await res.json();
    if (!Array.isArray(body.printers)) return [];
    return body.printers
      .map((p) => (typeof p === 'string' ? p : (p && p.name) || ''))
      .filter(Boolean);
  } catch {
    return [];
  } finally {
    done();
  }
}

// Three outcomes, and the caller can tell them apart:
//   printer configured + agent up   → { method: 'agent' }
//   printer configured + agent down → browserFallback(), onWarn(), { method: 'browser', warned: true }
//   no printer configured           → browserFallback() silently, { method: 'browser', warned: false }
// The middle case warns because the clinic expected silent printing and did not
// get it — that is worth telling them, not swallowing.
export async function printViaAgent(html, printerType, branch, opts = {}) {
  const { copies = 1, onWarn, browserFallback } = opts;
  const column = PRINTER_TYPE_COLUMN[printerType];
  const deviceName = column && branch ? (branch[column] || '') : '';

  if (deviceName) {
    try {
      if (!(await agentHealthy())) throw new Error('agent unreachable');
      // Abort a stalled print so a hung agent still falls back rather than
      // leaving the user staring at a dialog that never resolves.
      const { signal, done } = withTimeout(20000);
      try {
        const token = agentToken();
        const res = await fetch(`${AGENT_BASE}/print`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'X-Print-Agent-Token': token } : {}),
          },
          // `printer` is this project's name for the field. The ported agent
          // accepts `deviceName` too, but only this key is sent, so the wire
          // format has exactly one spelling.
          body: JSON.stringify({ html, printer: deviceName, copies }),
          signal,
        });
        if (!res.ok) throw new Error(`agent responded ${res.status}`);
        return { method: 'agent' };
      } finally {
        done();
      }
    } catch (err) {
      if (typeof browserFallback === 'function') browserFallback();
      if (typeof onWarn === 'function') onWarn(err);
      return { method: 'browser', warned: true };
    }
  }

  if (typeof browserFallback === 'function') browserFallback();
  return { method: 'browser', warned: false };
}
